<template>
  <div>
    <div v-show="false" ref="pdfTemplate" class="pb-report-pdf-template">
      <div class="pb-report-pdf-page">
        <header class="pb-report-pdf-page-header">
          <div class="pb-report-pdf-header">
            <div class="pb-report-pdf-logo-wrap">
              <img :src="logoUrl" alt="Dimitra Logo" class="pb-report-pdf-logo" />
            </div>
            <div class="pb-report-pdf-title-banner">
              <h2>{{ reportTitle }}</h2>
            </div>
          </div>
        </header>

        <div class="pb-report-pdf-content">
          <section class="pb-report-pdf-section pb-report-pdf-section--shaded">
            <div class="pb-report-pdf-grid">
              <div
                v-for="field in batchFields"
                :key="field.key"
                class="pb-report-pdf-cell"
                :class="{ 'pb-report-pdf-cell--empty': field.key === 'spacer' }"
              >
                <template v-if="field.key !== 'spacer'">
                  <div class="pb-report-pdf-cell__label">{{ field.label }}</div>
                  <div class="pb-report-pdf-cell__value">{{ field.value }}</div>
                </template>
              </div>
            </div>
          </section>

          <section class="pb-report-pdf-section pb-report-pdf-section--shaded">
            <h3 class="pb-report-pdf-section-title">{{ purchaseOrderLotsTitle }}</h3>
            <div
              v-for="(lot, index) in purchaseOrderLots"
              :key="`pb-lot-${index}`"
              class="pb-report-pdf-grid pb-report-pdf-grid--lot"
            >
              <div
                v-for="field in lot"
                :key="`${index}-${field.key}`"
                class="pb-report-pdf-cell"
              >
                <div class="pb-report-pdf-cell__label">{{ field.label }}</div>
                <div class="pb-report-pdf-cell__value">{{ field.value }}</div>
              </div>
            </div>
            <div v-if="!purchaseOrderLots.length" class="pb-report-pdf-grid pb-report-pdf-grid--lot">
              <div class="pb-report-pdf-cell">
                <div class="pb-report-pdf-cell__label">{{ lotIdLabel }}</div>
                <div class="pb-report-pdf-cell__value">—</div>
              </div>
              <div class="pb-report-pdf-cell">
                <div class="pb-report-pdf-cell__label">{{ farmerLabel }}</div>
                <div class="pb-report-pdf-cell__value">—</div>
              </div>
              <div class="pb-report-pdf-cell">
                <div class="pb-report-pdf-cell__label">{{ quantityLabel }}</div>
                <div class="pb-report-pdf-cell__value">—</div>
              </div>
              <div class="pb-report-pdf-cell">
                <div class="pb-report-pdf-cell__label">{{ priceLabel }}</div>
                <div class="pb-report-pdf-cell__value">—</div>
              </div>
            </div>
          </section>

          <section class="pb-report-pdf-section pb-report-pdf-section--qr">
            <h3 class="pb-report-pdf-section-title">{{ qrCodeTitle }}</h3>
            <div class="pb-report-pdf-qr-row">
              <div class="pb-report-pdf-qr-block">
                <div class="pb-report-pdf-qr-box">
                  <qrcode
                    v-if="activeReport.internalQrText"
                    :text="activeReport.internalQrText"
                    :size="112"
                  ></qrcode>
                </div>
                <p class="pb-report-pdf-qr-caption mb-0">
                  <span class="pb-report-pdf-qr-caption__text">
                    {{ internalIdLabel }}:
                    {{ activeReport.internalId || "—" }}
                  </span>
                </p>
              </div>
              <div class="pb-report-pdf-qr-block">
                <div class="pb-report-pdf-qr-box">
                  <qrcode
                    v-if="activeReport.externalQrText"
                    :text="activeReport.externalQrText"
                    :size="112"
                  ></qrcode>
                </div>
                <p class="pb-report-pdf-qr-caption mb-0">
                  <span class="pb-report-pdf-qr-caption__text">
                    {{ externalIdLabel }}:
                    {{ activeReport.externalId || "—" }}
                  </span>
                </p>
              </div>
            </div>
          </section>
        </div>

        <div class="pb-report-pdf-footer">
          <div class="pb-report-pdf-footer-left">Dimitra.io</div>
          <div class="pb-report-pdf-footer-right">
            {{ pageLabel(1) }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import html2canvas from "html2canvas";
import moment from "moment";

const PDF_PAGE_WIDTH_PX = 800;
const PDF_TOTAL_PAGES = 1;

export default {
  name: "ProcessingBatchReportPdfGenerator",
  data() {
    return {
      logoUrl: "/img/dimitra_logo_text.png",
      activeReport: this.emptyReport(),
    };
  },
  computed: {
    reportTitle() {
      return this.$t("memberDataSection.processingBatchReport.reportTitle");
    },
    purchaseOrderLotsTitle() {
      return this.$t("memberDataSection.processingBatchReport.purchaseOrderLots");
    },
    qrCodeTitle() {
      return this.$t("memberDataSection.greenCoffee.qrCode");
    },
    internalIdLabel() {
      return this.$t("memberDataSection.parchmentDelivery.bulkImport.internalId");
    },
    externalIdLabel() {
      return this.$t("memberDataSection.greenCoffee.externalId");
    },
    lotIdLabel() {
      return this.$t("memberDataSection.purchaseOrderDetail.lotId");
    },
    farmerLabel() {
      return this.$t("memberDataSection.farmer");
    },
    quantityLabel() {
      return this.$t("memberDataSection.processingBatchReport.quantity");
    },
    priceLabel() {
      return this.$t("price");
    },
    batchFields() {
      const d = this.activeReport;
      const t = (key) => this.$t(`memberDataSection.${key}`);
      return [
        { key: "lotId", label: this.lotIdLabel, value: d.lotId },
        { key: "startDate", label: t("startDate"), value: d.startDate },
        { key: "endDate", label: t("endDate"), value: d.endDate },
        { key: "totalCoffeeCherry", label: t("totalCoffeeCherry"), value: d.totalCoffeeCherryQty },
        { key: "humidity", label: t("airHumidity"), value: d.humidity },
        { key: "density", label: t("density"), value: d.density },
        { key: "temperature", label: t("temperature"), value: d.temperature },
        { key: "waterContent", label: t("beanMoistureContent"), value: d.waterContent },
        {
          key: "batchRating",
          label: this.$t("memberDataSection.processingBatchReport.ratingOfTheBatch"),
          value: d.batchRating,
        },
        { key: "processType", label: t("processType"), value: d.processType },
        { key: "parchmentOut", label: t("parchmentProduced"), value: d.parchmentOut },
        { key: "spacer", label: "", value: "" },
      ];
    },
    purchaseOrderLots() {
      return (this.activeReport.lots || []).map((lot) => [
        { key: "lotId", label: this.lotIdLabel, value: lot.lotId },
        { key: "farmer", label: this.farmerLabel, value: lot.farmer },
        { key: "quantity", label: this.quantityLabel, value: lot.quantity },
        { key: "price", label: this.priceLabel, value: lot.price },
      ]);
    },
  },
  methods: {
    emptyReport() {
      return {
        batchCode: "",
        lotId: "—",
        startDate: "—",
        endDate: "—",
        totalCoffeeCherryQty: "—",
        humidity: "—",
        density: "—",
        temperature: "—",
        waterContent: "—",
        batchRating: "—",
        processType: "—",
        parchmentOut: "—",
        lots: [],
        internalId: null,
        externalId: null,
        internalQrText: "",
        externalQrText: "",
      };
    },
    pageLabel(pageNumber) {
      return this.$t("memberDataSection.processingBatchReport.reportPageOf", {
        page: pageNumber,
        total: PDF_TOTAL_PAGES,
      });
    },
    async generatePdf(batch, listRecord = null) {
      if (!batch) {
        throw new Error(this.$t("memberDataSection.processingBatchReport.reportDataMissing"));
      }

      this.activeReport = this.buildReportData(batch, listRecord);
      await this.$nextTick();
      await new Promise((resolve) => setTimeout(resolve, 300));
      await this.createPdf();
    },
    buildReportData(batch, listRecord) {
      const row = listRecord || {};
      const merged = { ...row, ...batch };

      const internalId = this.resolveInternalId(merged, row);
      const externalId = this.resolveExternalId(merged, row);
      const orders = Array.isArray(merged.buyingStationOrder) ? merged.buyingStationOrder : [];

      return {
        batchCode: merged.batchCode || merged.id || "",
        lotId: merged.batchCode || merged.id || "—",
        startDate: this.formatDate(merged.startDate),
        endDate: this.formatDate(merged.endDate),
        totalCoffeeCherryQty:
          merged.totalCoffeeCherryQty != null ? `${merged.totalCoffeeCherryQty} kg` : "—",
        humidity: merged.humidity != null ? `${merged.humidity}%` : "—",
        density: merged.density != null ? `${merged.density} kg/m3` : "—",
        temperature: merged.temperature != null ? `${merged.temperature} C` : "—",
        waterContent: merged.waterContent != null ? `${merged.waterContent}%` : "—",
        batchRating: merged.batchRating || "—",
        processType: merged.processingType?.name || "—",
        parchmentOut: merged.parchmentOut != null ? `${merged.parchmentOut} kg` : "—",
        lots: orders.map((order) => this.mapLotRow(order)),
        internalId,
        externalId,
        internalQrText: merged.qrCode || internalId || "",
        // External QR opens the public traceability page; label still shows the id.
        externalQrText: merged.externalTraceabilityUrl || externalId || "",
      };
    },
    mapLotRow(order) {
      const person = order.farmer || order.buyingStation || {};
      const farmerName = [person.firstName, person.lastName].filter(Boolean).join(" ").trim();
      const currency = order.currency || "USD";
      const price =
        order.grandTotal != null && order.grandTotal !== ""
          ? `${currency} ${this.formatMoney(order.grandTotal)}`
          : "—";

      return {
        lotId: order.orderCode || "—",
        farmer: farmerName || "—",
        quantity:
          order.coffeeCherryQty != null ? `${order.coffeeCherryQty} kg` : "—",
        price,
      };
    },
    formatDate(value) {
      if (!value) return "—";
      if (moment(value, "MM/DD/YYYY", true).isValid()) {
        return moment(value, "MM/DD/YYYY").format("DD-MMM-YYYY").toUpperCase();
      }
      if (moment(value).isValid()) {
        return moment(value).format("DD-MMM-YYYY").toUpperCase();
      }
      return String(value).toUpperCase();
    },
    formatMoney(value) {
      const amount = Number(value);
      if (Number.isNaN(amount)) return String(value);
      return amount.toLocaleString("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      });
    },
    normalizeId(value) {
      const id = value != null ? String(value).trim() : "";
      return id || null;
    },
    resolveInternalId(batch, row) {
      return (
        this.normalizeId(batch.batchCode) ||
        this.normalizeId(batch.id) ||
        this.normalizeId(batch.internalId) ||
        this.normalizeId(batch.internal_id) ||
        this.normalizeId(row.batchCode) ||
        this.normalizeId(row.id)
      );
    },
    resolveExternalId(batch, row) {
      return (
        this.normalizeId(batch.external_id) ||
        this.normalizeId(batch.externalId) ||
        this.normalizeId(row.external_id) ||
        this.normalizeId(row.externalId)
      );
    },
    async createPdf() {
      const template = this.$refs.pdfTemplate;
      if (!template) {
        throw new Error("PDF template not found");
      }

      const originalDisplay = template.style.display;
      const originalVisibility = template.style.visibility;
      const originalPosition = template.style.position;
      const originalLeft = template.style.left;
      const originalWidth = template.style.width;

      template.style.display = "block";
      template.style.visibility = "visible";
      template.style.position = "absolute";
      template.style.left = "-9999px";
      template.style.width = `${PDF_PAGE_WIDTH_PX}px`;
      template.style.zIndex = "-1";

      try {
        await this.waitForPdfImages(template);
        await new Promise((resolve) => setTimeout(resolve, 200));

        const { jsPDF } = await import("jspdf");
        const pdf = new jsPDF("p", "mm", "a4");
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();
        const pages = template.querySelectorAll(".pb-report-pdf-page");

        for (let i = 0; i < pages.length; i += 1) {
          if (i > 0) pdf.addPage();
          const page = pages[i];
          const canvas = await html2canvas(page, {
            scale: 2,
            useCORS: true,
            allowTaint: false,
            logging: false,
            backgroundColor: "#ffffff",
            width: page.scrollWidth || PDF_PAGE_WIDTH_PX,
            height: page.scrollHeight || page.offsetHeight,
            windowWidth: PDF_PAGE_WIDTH_PX,
            imageTimeout: 30000,
            removeContainer: true,
          });

          const imgWidth = pageWidth;
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          pdf.addImage(
            canvas.toDataURL("image/png"),
            "PNG",
            0,
            0,
            imgWidth,
            Math.min(imgHeight, pageHeight)
          );
        }

        const batchCode = (this.activeReport.batchCode || "report").replace(/[^a-z0-9]/gi, "_");
        pdf.save(`Processing_Batch_Report_${batchCode}.pdf`);
      } finally {
        template.style.display = originalDisplay;
        template.style.visibility = originalVisibility;
        template.style.position = originalPosition;
        template.style.left = originalLeft;
        template.style.width = originalWidth;
        template.style.zIndex = "";
      }
    },
    async waitForPdfImages(template) {
      return new Promise((resolve) => {
        const images = template.querySelectorAll("img");
        if (!images.length) {
          resolve();
          return;
        }

        let loadedCount = 0;
        const totalImages = images.length;
        const timeout = setTimeout(() => resolve(), 10000);

        const checkComplete = () => {
          loadedCount += 1;
          if (loadedCount >= totalImages) {
            clearTimeout(timeout);
            resolve();
          }
        };

        images.forEach((img) => {
          img.style.display = "block";
          img.style.visibility = "visible";
          if (img.complete && img.naturalHeight !== 0) {
            checkComplete();
          } else {
            img.onload = checkComplete;
            img.onerror = checkComplete;
          }
        });
      });
    },
  },
};
</script>

<style lang="scss" scoped>
$pb-report-pad-x: 24px;
$pb-report-pad-bottom: 24px;
$pb-report-page-width: 800px;

.pb-report-pdf-template {
  width: $pb-report-page-width;
  margin: 0;
  padding: 0;
  background: #fff;
  font-family: Poppins, Arial, sans-serif;
}

.pb-report-pdf-page {
  position: relative;
  width: $pb-report-page-width;
  min-height: 1120px;
  padding: 0;
  margin: 0;
  box-sizing: border-box;
  background: #fff;
}

.pb-report-pdf-page-header {
  width: $pb-report-page-width;
  margin: 0 0 28px;
  padding: 6px 0 16px;
  box-sizing: border-box;
}

.pb-report-pdf-header {
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  width: $pb-report-page-width;
  column-gap: 40px;
  box-sizing: border-box;
}

.pb-report-pdf-logo-wrap {
  padding-left: $pb-report-pad-x;
  box-sizing: border-box;
}

.pb-report-pdf-logo {
  height: 48px;
  width: auto;
  max-width: 240px;
  object-fit: contain;
  display: block;
}

.pb-report-pdf-title-banner {
  width: 100%;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 14px 28px;
  background: #117443;
  border-radius: 8px 0 0 8px;
  box-sizing: border-box;
}

.pb-report-pdf-content {
  padding: 0 $pb-report-pad-x 72px;
  box-sizing: border-box;
}

.pb-report-pdf-title-banner h2 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 2px;
  color: #fff;
  text-align: center;
  white-space: nowrap;
  line-height: 1.2;
}

.pb-report-pdf-section {
  margin-bottom: 32px;
}

.pb-report-pdf-section--shaded {
  width: calc(100% + #{$pb-report-pad-x * 2});
  margin-left: -$pb-report-pad-x;
  margin-right: -$pb-report-pad-x;
  padding: 20px $pb-report-pad-x 24px;
  background: #f5f5f5;
  box-sizing: border-box;
}

.pb-report-pdf-section-title {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
  color: #212121;
}

.pb-report-pdf-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px 16px;
}

.pb-report-pdf-grid--lot + .pb-report-pdf-grid--lot {
  margin-top: 18px;
}

.pb-report-pdf-cell__label {
  font-size: 11px;
  font-weight: 500;
  color: #9e9e9e;
  margin-bottom: 6px;
  line-height: 1.25;
}

.pb-report-pdf-cell__value {
  font-size: 14px;
  font-weight: 700;
  color: #212121;
  line-height: 1.4;
  word-break: break-word;
}

.pb-report-pdf-qr-row {
  display: flex;
  flex-wrap: nowrap;
  gap: 32px;
}

.pb-report-pdf-qr-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.pb-report-pdf-qr-box {
  width: 120px;
  height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
}

.pb-report-pdf-qr-caption {
  text-align: center;
}

.pb-report-pdf-qr-caption__text {
  display: inline-block;
  font-size: 11px;
  font-weight: 600;
  color: #212121;
  white-space: nowrap;
}

.pb-report-pdf-footer {
  position: absolute;
  bottom: $pb-report-pad-bottom;
  left: $pb-report-pad-x;
  right: $pb-report-pad-x;
  display: flex;
  justify-content: space-between;
  padding-top: 12px;
  border-top: 1px solid #bdbdbd;
  font-size: 11px;
  color: #212121;
  box-sizing: border-box;
}

.pb-report-pdf-footer-left {
  font-weight: 600;
}

.pb-report-pdf-footer-right {
  color: #757575;
}
</style>
